import { useUser } from "@clerk/clerk-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAdminProgrammes } from "@/hooks/admin/programmes";

/**
 * Shared admin "scope": the cohort and assignment slot the admin is working
 * on. Persisted to localStorage so it survives navigation and reloads, and
 * surfaced by the AdminHeader switcher on routes that declare a scope in
 * adminNav. Page-local filters (status, user, topic area) stay on the pages.
 *
 * `programmeId: null` means "all cohorts" — pages whose queries require a
 * concrete programme fall back to `defaultProgrammeId` (see pickDefaultProgramme).
 */

const STORAGE_KEY = "dfr.admin.scope.v1";

export interface AdminScopeProgramme {
  id: string;
  name: string;
  /** Computed from start/end dates: "not_started" | "in_progress" | "completed". */
  status: string;
  start_date: string;
  is_draft: boolean;
  total_weeks: number;
  /** On Dominion Free: awards a participation badge, not a Certificate (KC-496). */
  is_freemium?: boolean;
}

/** Which cohort an admin is most likely to be working on, in priority order:
 * whatever is running; else what's about to start (so, prepping); else what
 * just ended (so, still marking).
 *
 * Deliberately *not* list order. The list arrives ordered by start_date desc
 * and now includes drafts (KC-387), so a future-dated draft sorts first — the
 * old `?? programmes[0]` fallback would silently default admins into an
 * unpublished cohort with no participants. Draft-ness doesn't enter this rule:
 * it's user-visibility state and shouldn't steer an admin default.
 */
function pickDefaultProgramme(programmes: AdminScopeProgramme[]): string | null {
  const byStart = (a: AdminScopeProgramme, b: AdminScopeProgramme, dir: 1 | -1) =>
    dir * (Date.parse(a.start_date) - Date.parse(b.start_date));

  // Running: most recently started.
  const running = programmes.filter((p) => p.status === "in_progress");
  if (running.length) return [...running].sort((a, b) => byStart(a, b, -1))[0].id;

  // Upcoming: soonest to start — the opposite of the dropdown's display order.
  const upcoming = programmes.filter((p) => p.status === "not_started");
  if (upcoming.length) return [...upcoming].sort((a, b) => byStart(a, b, 1))[0].id;

  // Finished: most recently ended, approximated by latest start.
  const completed = programmes.filter((p) => p.status === "completed");
  if (completed.length) return [...completed].sort((a, b) => byStart(a, b, -1))[0].id;

  return null;
}

interface StoredScope {
  programmeId: string | null;
  week: number | null;
  assignment: number | null;
}

interface AdminScopeValue extends StoredScope {
  /** The cohort most likely being worked on, for pages that need a concrete
   * one — running, else upcoming, else just-ended. See pickDefaultProgramme. */
  defaultProgrammeId: string | null;
  /** Cohort list for the switcher; empty for assessors (admin-only endpoint). */
  programmes: AdminScopeProgramme[];
  /** True while the cohort list is still loading (admins only). */
  isLoadingProgrammes: boolean;
  /** Assessors cannot list cohorts, so they get no cohort control. */
  canSwitchCohort: boolean;
  setProgrammeId: (id: string | null) => void;
  setSlot: (week: number | null, assignment: number | null) => void;
}

const AdminScopeContext = createContext<AdminScopeValue | null>(null);

function readStoredScope(): StoredScope | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      programmeId: typeof parsed.programmeId === "string" ? parsed.programmeId : null,
      week: typeof parsed.week === "number" ? parsed.week : null,
      assignment: typeof parsed.assignment === "number" ? parsed.assignment : null,
    };
  } catch {
    return null;
  }
}

function persistScope(scope: StoredScope) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
  } catch {
    // Storage unavailable (private mode etc.) — scope stays in-memory only.
  }
}

export function AdminScopeProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const isAssessor = (user?.publicMetadata?.role as string | undefined) === "assessor";

  const programmesQuery = useAdminProgrammes({ enabled: !isAssessor });
  const programmes = useMemo(
    () => (programmesQuery.data ?? []) as AdminScopeProgramme[],
    [programmesQuery.data]
  );

  // Hydrate once from storage; `hydratedFromStorage` distinguishes "user has
  // an explicit choice (including All)" from "first run, apply the default".
  const [stored] = useState(readStoredScope);
  const [scope, setScope] = useState<StoredScope>(
    stored ?? { programmeId: null, week: null, assignment: null }
  );
  const [hasExplicitChoice, setHasExplicitChoice] = useState(stored !== null);

  const defaultProgrammeId = useMemo(() => pickDefaultProgramme(programmes), [programmes]);

  // Validate the stored cohort against the loaded list: a deleted/renamed id
  // must never wedge a page, so it falls back to the default and re-persists.
  useEffect(() => {
    if (isAssessor || programmesQuery.isPending) return;
    setScope((prev) => {
      const isStale =
        prev.programmeId !== null && !programmes.some((p) => p.id === prev.programmeId);
      const applyDefault = (!hasExplicitChoice || isStale) && defaultProgrammeId !== null;
      if (!isStale && !applyDefault) return prev;
      const next = { ...prev, programmeId: applyDefault ? defaultProgrammeId : null };
      persistScope(next);
      return next;
    });
  }, [isAssessor, programmesQuery.isPending, programmes, defaultProgrammeId, hasExplicitChoice]);

  const setProgrammeId = useCallback((id: string | null) => {
    setHasExplicitChoice(true);
    setScope((prev) => {
      const next = { ...prev, programmeId: id };
      persistScope(next);
      return next;
    });
  }, []);

  const setSlot = useCallback((week: number | null, assignment: number | null) => {
    setHasExplicitChoice(true);
    setScope((prev) => {
      const next = { ...prev, week, assignment };
      persistScope(next);
      return next;
    });
  }, []);

  const value: AdminScopeValue = {
    ...scope,
    defaultProgrammeId,
    programmes,
    isLoadingProgrammes: !isAssessor && programmesQuery.isPending,
    canSwitchCohort: !isAssessor && programmes.length > 0,
    setProgrammeId,
    setSlot,
  };

  return <AdminScopeContext.Provider value={value}>{children}</AdminScopeContext.Provider>;
}

export function useAdminScope(): AdminScopeValue {
  const ctx = useContext(AdminScopeContext);
  if (!ctx) throw new Error("useAdminScope must be used within AdminScopeProvider");
  return ctx;
}
